import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { paymentsApi } from '../services/paymentsApi';
import { 
  CreditCard, Check, Zap, Crown, Building2, Sparkles, Calendar, Image as ImageIcon, 
  Users, RefreshCw, Loader2, Tag, X, ChevronDown, ChevronUp, Infinity
} from 'lucide-react';

const PricingPage = ({ onClose }) => {
  const { user } = useAuth();
  const [packages, setPackages] = useState(null);
  const [userCredits, setUserCredits] = useState(null);
  const [selectedTab, setSelectedTab] = useState('payperuse'); // payperuse, subscription, lifetime
  const [isLoading, setIsLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountResult, setDiscountResult] = useState(null);
  const [showAddOns, setShowAddOns] = useState(false);
  
  // Pay-per-use selections
  const [selectedCalendar, setSelectedCalendar] = useState('calendar_7');
  const [extraPlatforms, setExtraPlatforms] = useState(0);
  const [extraPosts, setExtraPosts] = useState(0);

  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const pkgs = await paymentsApi.getPackages();
      setPackages(pkgs);
      
      if (user?.uid) {
        const credits = await paymentsApi.getUserCredits(user.uid);
        setUserCredits(credits);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateDiscount = async (packageType, packageId) => {
    if (!discountCode) return;
    try {
      const result = await paymentsApi.validateDiscount(discountCode, packageType, packageId);
      setDiscountResult(result);
    } catch (error) {
      setDiscountResult({ valid: false, message: 'Error validating code' });
    }
  };

  const handleManageSubscription = async () => {
    if (!user?.uid) return;
    
    setIsLoadingPortal(true);
    try {
      const result = await paymentsApi.getCustomerPortalUrl(
        user.uid,
        window.location.href
      );
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Error opening portal:', error);
      alert(error.message || 'Failed to open subscription management. You may not have an active subscription.');
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handleCheckout = async (packageType, packageId) => {
    if (!user) {
      alert('Please sign in to make a purchase');
      return;
    }

    setCheckoutLoading(packageId);
    try {
      const result = await paymentsApi.createCheckout({
        package_type: packageType,
        package_id: packageId,
        user_id: user.uid,
        user_email: user.email,
        discount_code: discountCode || null,
        extra_platforms: packageType === 'calendar' ? extraPlatforms : 0,
        extra_posts: packageType === 'calendar' ? extraPosts : 0,
      });

      if (result.free) {
        alert('🎉 ' + result.message);
        loadData();
      } else if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Error creating checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const calculatePayPerUseTotal = () => {
    if (!packages) return 0;
    const base = packages.calendar_packages[selectedCalendar]?.price || 0;
    const platformCost = extraPlatforms * packages.add_ons.extra_platform.price;
    const postCost = extraPosts * packages.add_ons.extra_post.price;
    return (base + platformCost + postCost).toFixed(2);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900/95 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/95 overflow-y-auto" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Choose Your Plan</h1>
            <p className="text-slate-400 mt-1">Select the perfect plan for your content needs</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Current Credits Display */}
        {userCredits && userCredits.subscription_active && (
          <div className="mb-8 p-4 bg-indigo-900/30 border border-indigo-500/30 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-indigo-300">
                Your Current Plan: {userCredits.subscription_type?.replace('_', ' ').replace('ltd ', 'Lifetime ')}
                {userCredits.is_lifetime && <span className="ml-2 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">LIFETIME</span>}
              </h3>
              {!userCredits.is_lifetime && (
                <button
                  onClick={handleManageSubscription}
                  disabled={isLoadingPortal}
                  className="text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg flex items-center gap-1 disabled:opacity-50"
                  data-testid="manage-subscription-btn"
                >
                  {isLoadingPortal ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />}
                  Manage Subscription
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <Calendar className="w-4 h-4 text-indigo-400" />
                <span>Calendars: {userCredits.calendars_remaining === -1 ? '∞' : userCredits.calendars_remaining}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <Users className="w-4 h-4 text-indigo-400" />
                <span>Profiles: {userCredits.profiles_available}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <RefreshCw className="w-4 h-4 text-indigo-400" />
                <span>Content Regens: {userCredits.content_regens_remaining}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <ImageIcon className="w-4 h-4 text-indigo-400" />
                <span>Image Regens: {userCredits.image_regens_remaining}</span>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8 p-1 bg-slate-800 rounded-xl w-fit">
          <button
            onClick={() => setSelectedTab('payperuse')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              selectedTab === 'payperuse' 
                ? 'bg-indigo-600 text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Pay Per Use
          </button>
          <button
            onClick={() => setSelectedTab('subscription')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              selectedTab === 'subscription' 
                ? 'bg-indigo-600 text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Monthly Plans
          </button>
          <button
            onClick={() => setSelectedTab('lifetime')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
              selectedTab === 'lifetime' 
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Sparkles className="w-4 h-4" /> Lifetime Deals
          </button>
        </div>

        {/* Discount Code Input */}
        <div className="mb-8 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
          <div className="flex items-center gap-3">
            <Tag className="w-5 h-5 text-indigo-400" />
            <input
              type="text"
              value={discountCode}
              onChange={(e) => {
                setDiscountCode(e.target.value.toUpperCase());
                setDiscountResult(null);
              }}
              placeholder="Enter discount code"
              className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-indigo-500 outline-none"
            />
            {discountResult && (
              <span className={`text-sm ${discountResult.valid ? 'text-green-400' : 'text-red-400'}`}>
                {discountResult.valid 
                  ? discountResult.is_free 
                    ? '🎉 FREE!' 
                    : `${discountResult.discount_percent}% OFF!`
                  : discountResult.message
                }
              </span>
            )}
          </div>
        </div>

        {/* Pay Per Use Tab */}
        {selectedTab === 'payperuse' && packages && (
          <div className="space-y-6">
            {/* Calendar Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(packages.calendar_packages).map(([id, pkg]) => (
                <button
                  key={id}
                  onClick={() => setSelectedCalendar(id)}
                  className={`p-6 rounded-xl border-2 transition-all text-left ${
                    selectedCalendar === id
                      ? 'border-indigo-500 bg-indigo-900/20'
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-bold text-white">{pkg.name}</h3>
                    {selectedCalendar === id && <Check className="w-5 h-5 text-indigo-400" />}
                  </div>
                  <p className="text-3xl font-bold text-white mb-2">${pkg.price}</p>
                  <p className="text-sm text-slate-400">
                    {pkg.days} days • {pkg.platforms} platform{pkg.platforms > 1 ? 's' : ''} • {pkg.posts_per_day} post{pkg.posts_per_day > 1 ? 's' : ''}/day
                  </p>
                </button>
              ))}
            </div>

            {/* Add-ons Toggle */}
            <button
              onClick={() => setShowAddOns(!showAddOns)}
              className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300"
            >
              {showAddOns ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Customize with Add-ons
            </button>

            {/* Add-ons */}
            {showAddOns && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-800/30 rounded-xl">
                <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Extra Platforms</p>
                    <p className="text-xs text-slate-400">$1.00 each</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setExtraPlatforms(Math.max(0, extraPlatforms - 1))}
                      className="w-8 h-8 rounded bg-slate-700 text-white hover:bg-slate-600"
                    >-</button>
                    <span className="text-white w-8 text-center">{extraPlatforms}</span>
                    <button 
                      onClick={() => setExtraPlatforms(extraPlatforms + 1)}
                      className="w-8 h-8 rounded bg-slate-700 text-white hover:bg-slate-600"
                    >+</button>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Extra Posts/Day</p>
                    <p className="text-xs text-slate-400">$1.00 each (max 5 total)</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setExtraPosts(Math.max(0, extraPosts - 1))}
                      className="w-8 h-8 rounded bg-slate-700 text-white hover:bg-slate-600"
                    >-</button>
                    <span className="text-white w-8 text-center">{extraPosts}</span>
                    <button 
                      onClick={() => {
                        const currentPkg = packages?.calendar_packages?.[selectedCalendar];
                        const currentPosts = currentPkg?.posts_per_day || 1;
                        const maxExtra = 5 - currentPosts;
                        setExtraPosts(Math.min(maxExtra, extraPosts + 1));
                      }}
                      className="w-8 h-8 rounded bg-slate-700 text-white hover:bg-slate-600"
                    >+</button>
                  </div>
                </div>
              </div>
            )}

            {/* Total and Checkout */}
            <div className="flex items-center justify-between p-6 bg-slate-800 rounded-xl border border-slate-700">
              <div>
                <p className="text-slate-400 text-sm">Total</p>
                <p className="text-3xl font-bold text-white">${calculatePayPerUseTotal()}</p>
              </div>
              <button
                onClick={() => {
                  validateDiscount('calendar', selectedCalendar);
                  handleCheckout('calendar', selectedCalendar);
                }}
                disabled={checkoutLoading}
                className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-bold rounded-xl flex items-center gap-2 disabled:opacity-50"
              >
                {checkoutLoading === selectedCalendar ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <CreditCard className="w-5 h-5" />
                )}
                Purchase Calendar
              </button>
            </div>

            {/* Individual Add-ons */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(packages.add_ons).map(([id, addon]) => (
                <button
                  key={id}
                  onClick={() => handleCheckout('addon', id)}
                  disabled={checkoutLoading === id}
                  className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-indigo-500 transition-all text-left"
                >
                  <p className="text-white font-medium text-sm">{addon.name}</p>
                  <p className="text-indigo-400 font-bold">${addon.price}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Subscription Tab */}
        {selectedTab === 'subscription' && packages && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(packages.subscription_packages).map(([id, pkg]) => (
              <div
                key={id}
                className={`p-6 rounded-2xl border-2 transition-all ${
                  id === 'pro_monthly' 
                    ? 'border-indigo-500 bg-indigo-900/20 relative' 
                    : 'border-slate-700 bg-slate-800/50'
                }`}
              >
                {id === 'pro_monthly' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-indigo-600 rounded-full text-xs font-bold text-white">
                    MOST POPULAR
                  </div>
                )}
                <div className="flex items-center gap-2 mb-4">
                  {id === 'starter_monthly' && <Zap className="w-5 h-5 text-blue-400" />}
                  {id === 'pro_monthly' && <Crown className="w-5 h-5 text-indigo-400" />}
                  {id === 'agency_monthly' && <Building2 className="w-5 h-5 text-purple-400" />}
                  <h3 className="text-lg font-bold text-white">{pkg.name.replace(' Monthly', '')}</h3>
                </div>
                <p className="text-4xl font-bold text-white mb-1">${pkg.price}<span className="text-lg text-slate-400">/mo</span></p>
                <ul className="mt-6 space-y-3">
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-green-400" />
                    {pkg.calendars_per_month === -1 ? 'Unlimited' : pkg.calendars_per_month} calendars/month
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-green-400" />
                    {pkg.platforms === -1 ? 'All 6' : pkg.platforms} platforms
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-green-400" />
                    Up to {pkg.posts_per_day} posts/day/platform
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-green-400" />
                    {pkg.profiles} saved profiles
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-green-400" />
                    {pkg.content_regens} content regenerations
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-green-400" />
                    {pkg.image_regens} image regenerations
                  </li>
                </ul>
                <button
                  onClick={() => handleCheckout('subscription', id)}
                  disabled={checkoutLoading === id}
                  className={`w-full mt-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 ${
                    id === 'pro_monthly'
                      ? 'bg-indigo-600 hover:bg-indigo-500 text-white'
                      : 'bg-slate-700 hover:bg-slate-600 text-white'
                  }`}
                >
                  {checkoutLoading === id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Subscribe'
                  )}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Lifetime Deals Tab */}
        {selectedTab === 'lifetime' && packages && (
          <div className="space-y-6">
            {/* Launch Special Banner */}
            <div className="p-6 bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-2 border-amber-500/50 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="w-6 h-6 text-amber-400" />
                <h3 className="text-xl font-bold text-white">🚀 Launch Special - Limited Time!</h3>
              </div>
              <p className="text-slate-300 mb-4">Get lifetime access to FlowGen.social at a one-time price. No recurring fees, ever!</p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {Object.entries(packages.lifetime_packages).map(([id, pkg]) => (
                  <div
                    key={id}
                    className={`p-5 rounded-xl border transition-all ${
                      id === 'ltd_launch_special'
                        ? 'border-amber-500 bg-amber-900/20'
                        : 'border-slate-700 bg-slate-800/50'
                    }`}
                  >
                    {id === 'ltd_launch_special' && (
                      <div className="text-xs font-bold text-amber-400 mb-2">⭐ BEST VALUE</div>
                    )}
                    <h4 className="text-white font-bold mb-1">{pkg.name.replace('Lifetime Deal - ', '').replace(' - Lifetime', '')}</h4>
                    <p className="text-2xl font-bold text-white mb-3">${pkg.price}</p>
                    <ul className="space-y-1 text-xs text-slate-400 mb-4">
                      <li>{pkg.calendars_per_month === -1 ? '∞' : pkg.calendars_per_month} calendars/mo</li>
                      <li>{pkg.platforms === -1 ? 'All 6' : pkg.platforms} platforms</li>
                      <li>Up to {pkg.posts_per_day} posts/day</li>
                      <li>{pkg.profiles} profiles</li>
                      <li>{pkg.content_regens} content regens/mo</li>
                    </ul>
                    <button
                      onClick={() => handleCheckout('lifetime', id)}
                      disabled={checkoutLoading === id}
                      className={`w-full py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 ${
                        id === 'ltd_launch_special'
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black'
                          : 'bg-slate-700 hover:bg-slate-600 text-white'
                      }`}
                    >
                      {checkoutLoading === id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Get Lifetime Access'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Included in all packages */}
        <div className="mt-8 p-4 bg-slate-800/30 rounded-xl border border-slate-700">
          <h4 className="text-sm font-bold text-slate-400 mb-3">INCLUDED IN ALL PACKAGES:</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-slate-300">
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> AI Content Generation</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Real-time News Research</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> AI Image Generation</div>
            <div className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Multi-Platform Support</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
